import React from "react";

import BlockContent from "@sanity/block-content-to-react";

import { YouTube } from "./youtube";
import { Vimeo } from "./vimeo";
import { Video } from "./video";
import { ImageLink } from "./image-link";
import { ImageHeader } from "./image-header";
import { Quote } from "./quote";
import { Table } from "./table";
import { Divider } from "./divider";
import { IFrame } from "./iframe";
import { Code } from "./code";

import "./super-block-content.scss";
import { Snippet } from "./snippet";
import { kebabCase, urlFor } from "../../utils/utils";

import { getImageDimensions } from "@sanity/asset-utils";
import { Nest } from "./nest";
import { Grid } from "./grid";
import { ActionButton } from "../action-button";
import { Lottie } from "./lottie";
import { Cta } from "../cta";
import { Flex } from "./flex";
import { Rolodex } from "./rolodex";
import { Stats } from "./stats";
import Calculator from "../calculator";
import { Icon } from "./icon";
import { PrimaryList } from "./primary-list";
import { Wrapper } from "./wrapper";
import { EmailPrefs } from "../email-prefs-settings";
import { Timeline } from "./timeline";

const getTextFromChildren = (children: React.ReactNode) => {
  let text = "";
  React.Children.map(children, (child) => {
    if (typeof child === "string") {
      text += child;
    }
  });
  return text;
};

interface IProps {
  blocks: any;
  projectId: any;
  dataset: any;
  className: any;
}

const serializers = {
  types: {
    Component: (props: any) => {
      //TODO: This doesn't work, need to use groq or something else to resolve the reference
      // console.log("COMPONENT IS", props);
      return <SuperBlockContent {...props.node} />;
    },
    nest: (props: any) => <Nest {...props.node} />,
    grid: (props: any) => <Grid {...props.node} />,
    flex: (props: any) => <Flex {...props.node} />,
    "email-prefs": (props: any) => <EmailPrefs {...props.node} />,
    Rolodex: (props: any) => <Rolodex {...props.node} />, // TODO: Replace all instances of "Rolodex" with "rolodex"
    rolodex: (props: any) => <Rolodex {...props.node} />,
    stats: (props: any) => <Stats {...props.node} />,
    Calculator: (props: any) => <Calculator {...props.node} />, // TODO: Replace all instances of "Calculator" with "calculator"
    calculator: (props: any) => <Calculator {...props.node} />,
    "Primary List": (props: any) => <PrimaryList {...props.node} />, // TODO: Replace all instances of "Primary List" with "primaryList"
    primaryList: (props: any) => <PrimaryList {...props.node} />,
    wrapper: (props: any) => <Wrapper {...props.node} />,
    youtube: (props: any) => <YouTube {...props.node} />,
    vimeo: (props: any) => <Vimeo {...props.node} />,
    actionButton: (props: any) => <ActionButton {...props.node} />,
    video: (props: any) => <Video {...props.node} />,
    Icon: (props: any) => <Icon {...props.node} />, // TODO: Replace all instances of "Icon" with "icon"
    icon: (props: any) => <Icon {...props.node} />,
    Lottie: (props: any) => <Lottie {...props.node} />, // TODO: Replace all instances of "Lottie" with "lottie"
    lottie: (props: any) => <Lottie {...props.node} />,
    CTA: (props: any) => <Cta {...props.node} />, // TODO: Replace all instances of "CTA" with "cta"
    cta: (props: any) => <Cta {...props.node} />,
    imageLink: (props: any) => <ImageLink {...props.node} />,
    imageHeader: (props: any) => <ImageHeader {...props.node} />,
    quote: (props: any) => <Quote {...props.node} />,
    table: (props: any) => <Table {...props.node} />,
    divider: (props: any) => <Divider {...props.node} />,
    iframe: (props: any) => <IFrame {...props.node} />,
    code: (props: any) => <Code {...props.node} />,
    timeline: (props: any) => <Timeline {...props.node} />,
    snippet: (props: any) => <Snippet {...props.node} />,
    photo: (props: any) => {
      const maxWidth = props.node.maxWidth || 920;
      // console.log({ PHOTOPROPS: props });
      let { width, height, aspectRatio } = getImageDimensions(
        props.node.mainImage.asset,
      );
      const resizeFactor = maxWidth / width;
      return (
        <figure>
          <img
            className="photo-item"
            width={maxWidth}
            // height={height * resizeFactor}
            // style={{ aspectRatio: aspectRatio.toString() }}
            src={urlFor(props.node.mainImage.asset)
              .width(maxWidth)
              .format("webp")
              .url()}
            // srcSet={`${urlFor(props.node.mainImage.asset)
            //   .width(maxWidth)
            //   .format("webp")
            //   .url()} 920w,
            //   ${urlFor(props.node.mainImage.asset)
            //     .width(620)
            //     .format("webp")
            //     .url()} 620w,
            //   ${urlFor(props.node.mainImage.asset)
            //     .width(320)
            //     .format("webp")
            //     .url()} 320w,
            //   `}
            alt={props.node.title}
          />
        </figure>
      );
    },
    block: (props: any) => {
      // console.log("BLOCK:", props);
      const { node, children } = props;
      const { style, _key } = node;
      if (/^h\d/.test(style)) {
        const HeadingTag = style;
        const str = children.find((i: any) => typeof i === "string");
        let headingId = str ? `${kebabCase(str)}` : `h${_key}`;
        if (!/[A-z]/g.test(headingId[0])) headingId = `h_${headingId}`;
        // console.log({ props });
        return (
          <HeadingTag id={headingId}>
            <a href={`#${headingId}`} aria-hidden="true" tabIndex={-1}></a>
            <>{children}</>
          </HeadingTag>
        );
      }
      return BlockContent.defaultSerializers.types.block(props);
    },
  },
  marks: {
    link: (props: any) => {
      let { blank, href } = props.mark;
      const start = "https://hyperbrew.co";
      const isLocal = href.indexOf(start) === 0;
      // console.log({ isLocal });
      isLocal ? (href = href.replace(start, "")) : 0;
      return isLocal ? (
        <a href={href}>{props.children}</a>
      ) : (
        <a
          href={href}
          target={blank ? `_blank` : ""}
          rel={isLocal ? "" : "noopener"}
        >
          {props.children}
        </a>
      );
    },
  },
};

export const SuperBlockContent = ({
  blocks,
  projectId,
  dataset,
  className,
}: IProps) => (
  <BlockContent
    blocks={blocks}
    projectId={projectId}
    dataset={dataset}
    className={className || ""}
    serializers={serializers}
  />
);
